import { FormInstance } from 'antd';
import moment from 'moment';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { DatePicker } from 'components/atoms/date-picker';
import { Form } from 'components/atoms/form';
import { Modal } from 'components/atoms/modal';
import { Radio } from 'components/atoms/radio';
import { BodyShort01, Caption01 } from 'components/styles';
import { CostRateFormProps } from './drawer';

const StyledRadioGroup = styled(Radio.Group)`
  .ant-radio-wrapper {
    width: 100%;
    display: flex;
    align-items: flex-start;
  }
`;

const StyledRadio = styled(Radio)`
  margin-bottom: var(--spacing-03);

  > span {
    padding-right: 0;
  }
`;

const RadioLabelWrapper = styled.div`
  ${BodyShort01};
  color: var(--gray-100);
`;

const RadioDescriptionWrapper = styled.div`
  ${Caption01};
  color: var(--gray-80);
  margin: var(--spacing-02) 0;
  white-space: normal;
`;

const BackdateInputWrapper = styled.div`
  margin: var(--spacing-05) 0 0;
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
`;

interface Props {
  form: FormInstance<CostRateFormProps>;
  isVisible: boolean;
  isLoading: boolean;
  onClose: () => void;
  onSave: () => Promise<void>;
}

export const CostRateUpdateConfirmationModal: FC<Props> = ({
  form,
  isVisible,
  isLoading,
  onClose,
  onSave,
}) => {
  const { clearCostRate } = form.getFieldsValue();
  const [disableUpdate, setDisableUpdate] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  const today = useMemo(() => moment().startOf('day'), []);

  const updateRadioValue = useCallback(
    (value: boolean) => {
      setShowDatePicker(value);
      setDisableUpdate(value);
      form.setFieldsValue({
        shouldBackdate: value,
        backdateFrom: undefined,
      });
    },
    [form],
  );

  const disabledDate = useCallback(
    (date: moment.Moment) => date.isSameOrAfter(today, 'day'),
    [today],
  );

  const closeModal = useCallback(() => {
    onClose();
    setShowDatePicker(false);
    form.setFieldsValue({
      shouldBackdate: false,
      backdateFrom: undefined,
    });
  }, [form, onClose]);

  return (
    <Modal
      title={<FormattedMessage id="settings.cost_rates.update.modal.title" />}
      visible={isVisible}
      onOk={onSave}
      onCancel={closeModal}
      okButtonProps={{
        loading: isLoading,
        disabled: disableUpdate,
      }}
      okText={<FormattedMessage id="update" />}
      destroyOnClose
    >
      {clearCostRate ? (
        <FormattedMessage id="settings.cost_rates.update.modal.clear_cost.confirmation" />
      ) : (
        <Form form={form} layout="vertical">
          <Form.Item name="shouldBackdate">
            <StyledRadioGroup
              onChange={(e) => updateRadioValue(e.target.value)}
              defaultValue={false}
            >
              <StyledRadio value={false}>
                <RadioLabelWrapper>
                  <FormattedMessage id="settings.cost_rates.update.modal.future.label" />
                </RadioLabelWrapper>
                <RadioDescriptionWrapper>
                  <FormattedMessage id="settings.cost_rates.update.modal.future.description" />
                </RadioDescriptionWrapper>
              </StyledRadio>
              <StyledRadio value>
                <RadioLabelWrapper>
                  <FormattedMessage id="settings.cost_rates.update.modal.backdate.label" />
                </RadioLabelWrapper>
                <RadioDescriptionWrapper>
                  <FormattedMessage id="settings.cost_rates.update.modal.future.description" />
                </RadioDescriptionWrapper>
                {showDatePicker && (
                  <BackdateInputWrapper>
                    <Form.Item
                      name="backdateFrom"
                      rules={[
                        {
                          required: true,
                        },
                      ]}
                    >
                      <StyledDatePicker
                        allowClear={false}
                        disabledDate={disabledDate}
                        disabled={false}
                        onOpenChange={(open) => {
                          const { backdateFrom } = form.getFieldsValue();

                          setDisableUpdate(open || backdateFrom == null);
                        }}
                      />
                    </Form.Item>
                  </BackdateInputWrapper>
                )}
              </StyledRadio>
            </StyledRadioGroup>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};
